import { useMemo, useState } from "react";
import {
  C, GlassCard, Badge, Button, ProgressBar, StatusDot, Tabs,
} from "@coremonit/design-system";
import {
  GitBranch, Boxes, Server, Play, Pause, SkipBack, SkipForward,
  AlertTriangle, ChevronRight, ChevronDown, Terminal, Network, Layers,
} from "lucide-react";
import type { DeploymentTimelineEntry, DeploymentResourceNode } from "@coremonit/types";

interface Props {
  entry: DeploymentTimelineEntry;
  compact?: boolean;
}

export function DeploymentViewer({ entry, compact }: Props) {
  const [tab, setTab] = useState(entry.success ? "resources" : "summary");
  const [selectedResource, setSelectedResource] = useState<string>("dep_root");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["dep_root", "rs_root"]));
  const [step, setStep] = useState(entry.events.length - 1);
  const [playing, setPlaying] = useState(false);

  const phases = useMemo(() => buildPhases(entry), [entry]);
  const currentPhase = phases[Math.min(step, phases.length - 1)] ?? phases[0];

  const findResource = (nodes: DeploymentResourceNode[] | undefined, id: string): DeploymentResourceNode | null => {
    if (!nodes) return null;
    for (const n of nodes) {
      if (n.id === id) return n;
      const found = findResource(n.children, id);
      if (found) return found;
    }
    return null;
  };

  const selected = findResource(entry.resourceTree, selectedResource);

  const tabs = [
    { id: "summary", label: "Summary" },
    { id: "resources", label: "Resources" },
    { id: "events", label: "Events" },
    ...(entry.podLogs?.length ? [{ id: "logs", label: "Logs" }] : []),
    { id: "rollout", label: "Rollout" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* ArgoCD-style status header */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <StatusDot status={entry.healthStatus ?? (entry.success ? "healthy" : "critical")} pulse={!entry.success} size={12} />
        <span style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{entry.serviceName}</span>
        <Badge color="blue">{entry.revision}</Badge>
        <Badge color={entry.syncStatus === "synced" ? "green" : "amber"}>{entry.syncStatus ?? (entry.success ? "synced" : "outofsync")}</Badge>
        <Badge color={entry.success ? "green" : "red"}>{entry.success ? "Healthy" : "Degraded"}</Badge>
        <Badge color="muted">{entry.strategy.replace("_", " ")}</Badge>
      </div>

      {!entry.success && entry.failureReason && (
        <GlassCard style={{ padding: 14, border: `1px solid ${C.red}55` }} accent={`${C.red}33`}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <AlertTriangle size={18} color={C.red} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: C.red, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Deployment failed</div>
              <div style={{ color: C.text, fontSize: 12, lineHeight: 1.5 }}>{entry.failureReason}</div>
              {entry.errorDetails && (
                <ul style={{ margin: "10px 0 0", paddingLeft: 18, color: C.textSub, fontSize: 11, lineHeight: 1.6 }}>
                  {entry.errorDetails.map((e) => <li key={e}><code style={{ fontFamily: "ui-monospace, monospace", fontSize: 10 }}>{e}</code></li>)}
                </ul>
              )}
            </div>
          </div>
        </GlassCard>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, fontSize: 10 }}>
        <Meta label="Image" value={entry.image ?? entry.imageTag} mono />
        <Meta label="Git" value={`${entry.gitBranch ?? "main"} · ${entry.gitCommit?.slice(0, 7) ?? "—"}`} mono />
        <Meta label="Namespace" value={entry.namespace ?? "default"} />
        <Meta label="Cluster" value={entry.cluster ?? "—"} />
        <Meta label="Duration" value={`${entry.durationSec}s`} />
        <Meta label="Triggered by" value={entry.triggeredBy ?? "—"} />
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === "summary" && (
        <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: 12 }}>
          <GlassCard style={{ padding: 14 }}>
            <SectionLabel title="Rollout status" />
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
              <Row label="Pods replaced" value={String(entry.podsReplaced)} />
              <Row label="Health checks passed" value={String(entry.healthChecksPassed)} />
              {entry.trafficShiftPct != null && (
                <>
                  <Row label="Traffic shift" value={`${entry.trafficShiftPct}%`} />
                  <ProgressBar value={entry.trafficShiftPct} color={entry.success ? C.cyan : C.red} />
                </>
              )}
              <Row label="Started" value={new Date(entry.startedAt).toLocaleString()} />
              <Row label="Completed" value={entry.completedAt ? new Date(entry.completedAt).toLocaleString() : "—"} />
            </div>
          </GlassCard>
          <GlassCard style={{ padding: 14 }}>
            <SectionLabel title="Recent events" />
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4, maxHeight: 200, overflow: "auto" }}>
              {entry.events.slice(-6).reverse().map((e, i) => (
                <EventRow key={i} kind={e.kind} message={e.message} ts={e.ts} detail={e.detail} />
              ))}
            </div>
          </GlassCard>
        </div>
      )}

      {tab === "resources" && (
        <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "240px 1fr", gap: 12, minHeight: 280 }}>
          <GlassCard style={{ padding: 10, overflow: "auto", maxHeight: 360 }}>
            <SectionLabel title="Resource tree" />
            <div style={{ marginTop: 8 }}>
              {(entry.resourceTree ?? []).map((n) => (
                <ResourceTreeNode
                  key={n.id}
                  node={n}
                  depth={0}
                  expanded={expanded}
                  selected={selectedResource}
                  onToggle={(id) => setExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id); else next.add(id);
                    return next;
                  })}
                  onSelect={setSelectedResource}
                />
              ))}
            </div>
          </GlassCard>
          <GlassCard style={{ padding: 14 }}>
            {selected ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <KindIcon kind={selected.kind} />
                  <span style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{selected.name}</span>
                  <Badge color={selected.health === "healthy" ? "green" : selected.health === "warning" ? "amber" : "red"}>{selected.health}</Badge>
                </div>
                {selected.message && (
                  <div style={{ color: C.textSub, fontSize: 11, marginBottom: 12, padding: 10, background: C.surfaceHigh, borderRadius: 8 }}>
                    {selected.message}
                  </div>
                )}
                <SectionLabel title="Conditions" />
                <div style={{ marginTop: 8 }}>
                  <Row label="Status" value={selected.status} />
                  <Row label="Kind" value={selected.kind} />
                </div>
              </>
            ) : (
              <div style={{ color: C.textMuted, fontSize: 12, padding: 20, textAlign: "center" }}>Select a resource</div>
            )}
          </GlassCard>
        </div>
      )}

      {tab === "events" && (
        <GlassCard style={{ padding: 14, maxHeight: 400, overflow: "auto" }}>
          <SectionLabel title="Event timeline" />
          <div style={{ marginTop: 12, position: "relative", paddingLeft: 20 }}>
            <div style={{ position: "absolute", left: 6, top: 4, bottom: 4, width: 1, background: C.border }} />
            {entry.events.map((e, i) => (
              <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14, position: "relative" }}>
                <div style={{
                  position: "absolute", left: -20, top: 4, width: 12, height: 12, borderRadius: "50%",
                  background: eventColor(e.kind),
                  boxShadow: `0 0 8px ${eventColor(e.kind)}`,
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Badge color={eventBadge(e.kind)}>{e.kind}</Badge>
                    <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{e.message}</span>
                  </div>
                  {e.resource && <div style={{ color: C.blue, fontSize: 10, fontFamily: "ui-monospace, monospace", marginTop: 2 }}>{e.resource}</div>}
                  {e.detail && <div style={{ color: C.textMuted, fontSize: 10, marginTop: 2 }}>{e.detail}</div>}
                  <div style={{ color: C.textMuted, fontSize: 9, marginTop: 4 }}>{new Date(e.ts).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {tab === "logs" && entry.podLogs && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {entry.podLogs.map((pl) => (
            <GlassCard key={pl.pod} style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: C.surfaceHigh, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 6 }}>
                <Terminal size={12} color={C.red} />
                <span style={{ color: C.text, fontSize: 11, fontWeight: 600 }}>{pl.pod}</span>
                <span style={{ color: C.textMuted, fontSize: 10 }}>/ {pl.container}</span>
              </div>
              <pre style={{ margin: 0, padding: 12, fontSize: 11, lineHeight: 1.5, color: C.textSub, fontFamily: "ui-monospace, Menlo, monospace", overflow: "auto", maxHeight: 220, background: C.bg }}>
                {pl.lines.map((line, i) => (
                  <div key={i} style={{ color: line.includes("[ERROR]") || line.includes("[FATAL]") ? C.red : line.includes("[WARN]") ? C.amber : C.textSub }}>{line}</div>
                ))}
              </pre>
            </GlassCard>
          ))}
        </div>
      )}

      {tab === "rollout" && (
        <GlassCard style={{ padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <SectionLabel title="Live rollout playback" />
            <div style={{ display: "flex", gap: 4 }}>
              <Button variant="ghost" size="xs" icon={<SkipBack size={11} />} onClick={() => { setStep(Math.max(0, step - 1)); setPlaying(false); }} />
              {playing
                ? <Button variant="secondary" size="xs" icon={<Pause size={11} />} onClick={() => setPlaying(false)} />
                : <Button variant="glow" size="xs" icon={<Play size={11} />} onClick={() => setPlaying(true)} />}
              <Button variant="ghost" size="xs" icon={<SkipForward size={11} />} onClick={() => { setStep(Math.min(phases.length - 1, step + 1)); setPlaying(false); }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <PhaseBox title="Old pods" items={currentPhase.oldPods} tone="red" />
            <PhaseBox title="New pods" items={currentPhase.newPods} tone="green" />
          </div>
          <div style={{ color: C.textSub, fontSize: 10, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>
            Step {step + 1} / {phases.length} · {currentPhase.label}
          </div>
          <ProgressBar value={((step + 1) / phases.length) * 100} color={entry.success ? C.green : C.red} />
        </GlassCard>
      )}
    </div>
  );
}

function ResourceTreeNode({
  node, depth, expanded, selected, onToggle, onSelect,
}: {
  node: DeploymentResourceNode; depth: number;
  expanded: Set<string>; selected: string;
  onToggle: (id: string) => void; onSelect: (id: string) => void;
}) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isOpen = expanded.has(node.id);
  const isSelected = selected === node.id;

  return (
    <div>
      <button
        onClick={() => { onSelect(node.id); if (hasChildren) onToggle(node.id); }}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 4,
          padding: "4px 6px", paddingLeft: depth * 14 + 4,
          background: isSelected ? C.blueDim : "transparent",
          border: `1px solid ${isSelected ? C.borderBright : "transparent"}`,
          borderRadius: 6, cursor: "pointer", textAlign: "left",
        }}
      >
        {hasChildren ? (isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />) : <span style={{ width: 11 }} />}
        <KindIcon kind={node.kind} small />
        <span style={{ color: C.textSub, fontSize: 10, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
        <StatusDot status={node.health} />
      </button>
      {hasChildren && isOpen && node.children!.map((c) => (
        <ResourceTreeNode key={c.id} node={c} depth={depth + 1} expanded={expanded} selected={selected} onToggle={onToggle} onSelect={onSelect} />
      ))}
    </div>
  );
}

function KindIcon({ kind, small }: { kind: DeploymentResourceNode["kind"]; small?: boolean }) {
  const size = small ? 10 : 12;
  const color = C.blue;
  switch (kind) {
    case "deployment": return <Layers size={size} color={color} />;
    case "replicaset": return <Boxes size={size} color={color} />;
    case "pod": return <Boxes size={size} color={color} />;
    case "service": return <Network size={size} color={color} />;
    case "ingress": return <GitBranch size={size} color={color} />;
    default: return <Server size={size} color={color} />;
  }
}

function EventRow({ kind, message, ts, detail }: { kind: string; message: string; ts: string; detail?: string }) {
  return (
    <div style={{ padding: "6px 8px", background: C.surfaceHigh, borderRadius: 6, borderLeft: `3px solid ${eventColor(kind)}` }}>
      <div style={{ color: C.text, fontSize: 11 }}>{message}</div>
      {detail && <div style={{ color: C.textMuted, fontSize: 10, marginTop: 2 }}>{detail}</div>}
      <div style={{ color: C.textMuted, fontSize: 9, marginTop: 2 }}>{new Date(ts).toLocaleString()}</div>
    </div>
  );
}

function eventColor(kind: string) {
  if (kind === "failed" || kind === "degraded") return C.red;
  if (kind === "warning") return C.amber;
  if (kind === "synced" || kind === "progressed") return C.green;
  return C.blue;
}

function eventBadge(kind: string): "green" | "red" | "amber" | "blue" | "muted" {
  if (kind === "failed" || kind === "degraded") return "red";
  if (kind === "warning") return "amber";
  if (kind === "synced") return "green";
  if (kind === "progressed") return "blue";
  return "muted";
}

function buildPhases(entry: DeploymentTimelineEntry) {
  const n = Math.max(entry.podsReplaced, 3);
  return Array.from({ length: 5 }, (_, i) => {
    const replaced = Math.round((i / 4) * n);
    const remaining = n - replaced;
    const labels = ["Image pull", "Pod creation", "Readiness checks", "Traffic shift", "Rollout complete"];
    return {
      label: labels[i],
      oldPods: Array.from({ length: Math.max(0, remaining) }, (_, j) => `${entry.serviceName}-old-${j + 1}`),
      newPods: Array.from({ length: replaced }, (_, j) => `${entry.serviceName}-new-${j + 1}`),
    };
  });
}

function PhaseBox({ title, items, tone }: { title: string; items: string[]; tone: "green" | "red" }) {
  const color = tone === "green" ? C.green : C.red;
  return (
    <div style={{ padding: 10, background: C.surfaceHigh, borderRadius: 10, border: `1px solid ${color}33`, minHeight: 80 }}>
      <div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{title}</div>
      {items.length === 0
        ? <div style={{ color: C.textMuted, fontSize: 10 }}>—</div>
        : items.map((p) => (
          <div key={p} style={{ color, fontSize: 10, fontFamily: "ui-monospace, monospace", padding: "2px 0" }}>{p}</div>
        ))}
    </div>
  );
}

function SectionLabel({ title }: { title: string }) {
  return <div style={{ color: C.textSub, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>;
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ padding: 8, background: C.surfaceHigh, borderRadius: 8 }}>
      <div style={{ color: C.textMuted, fontSize: 8, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: C.text, fontSize: 10, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: mono ? "ui-monospace, monospace" : undefined }}>{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "4px 0", borderBottom: `1px solid ${C.border}` }}>
      <span style={{ color: C.textMuted }}>{label}</span>
      <span style={{ color: C.text, fontFamily: "ui-monospace, monospace" }}>{value}</span>
    </div>
  );
}
